import {
  STAFF,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  type CursorPage,
  type Notification,
} from '@legalcrm/api';
import { Avatar, Badge, Button, Drawer, Dropdown } from '@legalcrm/ui';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/AuthProvider';
import { openAssistant, useAssistantDoodleHidden } from '@/shell/assistantVisibility';
import { useTheme } from '@/shell/theme';

import { Breadcrumbs } from './Breadcrumbs';

export function TopBar({ onOpenSidebar }: { onOpenSidebar: () => void }) {
  const { me, signOut } = useAuth();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [doodleHidden, setDoodleHidden] = useAssistantDoodleHidden();
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b bg-surface/95 px-4 backdrop-blur">
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={onOpenSidebar}
        aria-label="Open navigation"
      >
        <svg
          className="h-5 w-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      </Button>

      <Breadcrumbs className="hidden min-w-0 flex-1 md:flex" />

      <div className="ml-auto flex items-center gap-1">
        <NotificationBell onOpen={() => setNotificationsOpen(true)} />

        <Dropdown
          label="Account"
          trigger={() => (
            <span className="flex items-center gap-2 rounded p-1 hover:bg-surface-sunken">
              <Avatar name={me?.user.full_name ?? 'Account'} size="sm" />
            </span>
          )}
          items={[
            {
              key: 'profile',
              label: me?.user.email ?? 'Profile',
              onSelect: () => navigate('/settings/profile'),
            },
            {
              key: 'theme',
              label: theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme',
              onSelect: () => setTheme(theme === 'dark' ? 'light' : 'dark'),
              separatorBefore: true,
            },
            {
              key: 'assistant-doodle',
              label: doodleHidden ? 'Show assistant character' : 'Hide assistant character',
              onSelect: () => setDoodleHidden(!doodleHidden),
            },
            ...(doodleHidden
              ? [{ key: 'assistant-open', label: 'Ask the assistant', onSelect: openAssistant }]
              : []),
            {
              key: 'signout',
              label: 'Sign out',
              destructive: true,
              separatorBefore: true,
              onSelect: () => {
                void signOut().then(() => navigate('/login'));
              },
            },
          ]}
        />
      </div>

      <NotificationDrawer open={notificationsOpen} onClose={() => setNotificationsOpen(false)} />
    </header>
  );
}

/**
 * PLACEHOLDER. Global search is server-side and permission-filtered (S-A03) — results
 * must be scoped by the same resolvers as record access, which means it cannot be a
 * client-side filter over anything already loaded.
 *
 * NOT RENDERED. It sat in the header of every screen reading "Search — coming soon",
 * which is a promise the product makes on every page and keeps on none. A disabled
 * control in the most prominent position in the application teaches people that
 * things here do not work, and they carry that suspicion to the parts that do.
 *
 * Kept rather than deleted because the reasoning above is the hard part and should
 * not have to be rediscovered. Render it again from `TopBar` on the day the search
 * endpoint exists.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function GlobalSearch() {
  return (
    <div className="relative hidden w-full max-w-xs md:block">
      <svg
        className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" strokeLinecap="round" />
      </svg>
      <input
        type="search"
        disabled
        placeholder="Search — coming soon"
        aria-label="Search matters, clients and documents"
        className="h-8 w-full rounded border border-line-strong bg-surface-sunken pl-8 pr-3 text-sm text-ink placeholder:text-ink-subtle disabled:cursor-not-allowed"
      />
    </div>
  );
}

function NotificationBell({ onOpen }: { onOpen: () => void }) {
  const { data } = useApiQuery<CursorPage<Notification>>(qk.notifications(), '/notifications', {
    // A bell that hammers the API on every focus is worse than a bell that is a
    // minute stale. But it must not be stale forever: polled on the sidebar's cadence,
    // so a client replying or signing reaches it without anyone navigating away.
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
  const unread = data?.results.filter((item) => !item.read_at).length ?? 0;

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onOpen}
      aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
      className="relative"
    >
      <svg
        className="h-5 w-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <path d="M18 8a6 6 0 1 0-12 0c0 6-2 7-2 7h16s-2-1-2-7M10.5 20a1.8 1.8 0 0 0 3 0" />
      </svg>
      {unread > 0 && (
        <span
          className="absolute -right-0.5 -top-0.5 flex h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full bg-danger px-1 text-[0.6875rem] font-semibold leading-none text-white ring-2 ring-surface"
          aria-hidden="true"
        >
          {unread > 9 ? '9+' : unread}
        </span>
      )}
    </Button>
  );
}

function NotificationDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const api = useApi();
  const { data, isLoading } = useApiQuery<CursorPage<Notification>>(
    qk.notifications(),
    '/notifications',
    { enabled: open },
  );
  const items = data?.results ?? [];

  // Opening the drawer is reading them. Without this the bell, which has just started
  // working, would light up once and stay lit for good -- and a permanent badge is one
  // people stop seeing within a day.
  //
  // The list is deliberately NOT invalidated on success: re-fetching underneath somebody
  // who is still reading would blank the entries they came to read. The badge clears on
  // the next natural refetch, which is the right moment.
  const clear = useApiMutation<{ marked_read: number }, void>(
    { mutationFn: () => api.post(`${STAFF.notificationsReadAll}/`) },
    {},
  );

  const clearRef = useRef(clear.mutate);
  clearRef.current = clear.mutate;
  useEffect(() => {
    if (open && items.some((item) => !item.read_at)) clearRef.current();
    // Keyed on `open` alone: this fires once per opening, not once per arriving item.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Drawer open={open} onClose={onClose} title="Notifications" size="md">
      {isLoading && <p className="text-sm text-ink-muted">Loading…</p>}
      {!isLoading && items.length === 0 && (
        <p className="py-8 text-center text-sm text-ink-muted">Nothing new.</p>
      )}
      <ul className="flex flex-col divide-y">
        {items.map((item) => (
          <li key={item.id} className="flex flex-col gap-1 py-3">
            <div className="flex items-center gap-2">
              <p className="text-base font-medium text-ink">{item.title}</p>
              {!item.read_at && (
                <Badge tone="primary" size="sm">
                  New
                </Badge>
              )}
            </div>
            <p className="text-sm text-ink-muted">{item.body}</p>
            <time dateTime={item.created_at} className="text-xs text-ink-subtle">
              {new Date(item.created_at).toLocaleString()}
            </time>
          </li>
        ))}
      </ul>
    </Drawer>
  );
}
